/**
 * Standardized GA4 Event Tracking Utilities
 * Implements consistent event tracking following GA4 naming conventions
 * and standardized parameter structures for Twisted Cloud Solutions
 */

import { pushToDataLayer, isGTMReady } from './gtm';

/**
 * Get session ID for consistent tracking
 * @returns {string} Session identifier
 */
const getSessionId = () => {
  if (typeof window === 'undefined') return 'server';
  
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Get global parameters included with all events
 * @returns {Object} Global tracking parameters
 */
const getGlobalParameters = () => {
  const now = new Date();
  return {
    timestamp: now.toISOString(),
    session_id: getSessionId(),
    page_title: document.title,
    page_path: window.location.pathname,
    page_location: window.location.href,
    page_referrer: document.referrer,
    user_agent: navigator.userAgent,
    device_type: window.innerWidth <= 768 ? 'mobile' : 'desktop',
    gtm_container_id: window.GTM_CONTAINER_ID || 'GTM-XXXXXXX',
    ga_measurement_id: window.GA_MEASUREMENT_ID || 'G-429009383'
  };
};

/**
 * Validate event name follows naming convention
 * @param {string} eventName - Event name to validate
 * @returns {boolean} True if valid
 */
const validateEventName = (eventName) => {
  // Must be snake_case, lowercase, max 40 characters
  const namePattern = /^[a-z][a-z0-9_]*[a-z0-9]$/;
  return eventName && 
         typeof eventName === 'string' && 
         eventName.length <= 40 && 
         namePattern.test(eventName);
};

/**
 * Validate parameter name follows naming convention
 * @param {string} paramName - Parameter name to validate
 * @returns {boolean} True if valid
 */
const validateParameterName = (paramName) => {
  // Must be snake_case, lowercase, max 40 characters
  const namePattern = /^[a-z][a-z0-9_]*[a-z0-9]$/;
  return paramName && 
         typeof paramName === 'string' && 
         paramName.length <= 40 && 
         namePattern.test(paramName);
};

/**
 * Sanitize parameter value to prevent PII and ensure proper formatting
 * @param {*} value - Parameter value to sanitize
 * @returns {*} Sanitized value
 */
const sanitizeParameterValue = (value) => {
  if (typeof value === 'string') {
    // Remove potential PII patterns (emails, phones, etc.)
    value = value.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[email]');
    value = value.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '[phone]');
    
    // Truncate long strings
    if (value.length > 100) {
      value = value.substring(0, 97) + '...';
    }
  }
  return value;
};

/**
 * Track call-to-action button clicks with standardized parameters
 * @param {string} ctaId - Unique button identifier (required)
 * @param {string} ctaText - Button text content (required)
 * @param {Object} options - Additional tracking options
 * @param {string} options.sectionId - Page section identifier
 * @param {string} options.ctaType - Button type/style (primary, secondary, etc.)
 * @param {string} options.campaignId - Marketing campaign identifier
 * @param {string} options.abTestVariant - A/B testing variant
 * 
 * @example
 * trackCtaClick('start-project', 'Start Your Project', {
 *   sectionId: 'hero-main',
 *   ctaType: 'primary'
 * });
 */
export const trackCtaClick = (ctaId, ctaText, options = {}) => {
  // Validation
  if (!ctaId || !ctaText) {
    console.warn('trackCtaClick: ctaId and ctaText are required');
    return;
  }

  if (!isGTMReady()) {
    console.warn('trackCtaClick: GTM Data Layer not ready');
    return;
  }

  // Build event data
  const eventData = {
    event: 'cta_click',
    event_category: 'engagement',
    event_action: 'click',
    // Required parameters
    cta_id: sanitizeParameterValue(ctaId),
    cta_text: sanitizeParameterValue(ctaText),
    // Optional parameters
    section_id: options.sectionId ? sanitizeParameterValue(options.sectionId) : undefined,
    cta_type: options.ctaType ? sanitizeParameterValue(options.ctaType) : 'default',
    campaign_id: options.campaignId ? sanitizeParameterValue(options.campaignId) : undefined,
    ab_test_variant: options.abTestVariant ? sanitizeParameterValue(options.abTestVariant) : undefined,
    // Global parameters
    ...getGlobalParameters()
  };

  // Remove undefined values
  Object.keys(eventData).forEach(key => {
    if (eventData[key] === undefined) delete eventData[key];
  });

  // Send to data layer
  pushToDataLayer(eventData);

  // Development logging
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 CTA Click Event:', eventData);
  }
};

/**
 * Track form submission events with standardized parameters
 * @param {string} formId - Form identifier (required)
 * @param {string} formName - Form display name (required)
 * @param {Object} options - Additional tracking options
 * @param {string} options.formType - Form category (contact, newsletter, etc.)
 * @param {number} options.formFields - Number of fields completed
 * @param {number} options.validationErrors - Number of validation errors
 * @param {number} options.timeToComplete - Time spent on form (seconds)
 * @param {string} options.leadScore - Calculated lead value
 * 
 * @example
 * trackFormSubmit('contact-form', 'Contact Us', {
 *   formType: 'contact',
 *   formFields: 5,
 *   timeToComplete: 120
 * });
 */
export const trackFormSubmit = (formId, formName, options = {}) => {
  // Validation
  if (!formId || !formName) {
    console.warn('trackFormSubmit: formId and formName are required');
    return;
  }

  if (!isGTMReady()) {
    console.warn('trackFormSubmit: GTM Data Layer not ready');
    return;
  }

  // Build event data
  const eventData = {
    event: 'form_submit',
    event_category: 'conversion',
    event_action: 'submit',
    // Required parameters
    form_id: sanitizeParameterValue(formId),
    form_name: sanitizeParameterValue(formName),
    // Optional parameters
    form_type: options.formType ? sanitizeParameterValue(options.formType) : 'general',
    form_fields: options.formFields ? Number(options.formFields) : undefined,
    validation_errors: options.validationErrors ? Number(options.validationErrors) : undefined,
    time_to_complete: options.timeToComplete ? Number(options.timeToComplete) : undefined,
    lead_score: options.leadScore ? sanitizeParameterValue(options.leadScore) : undefined,
    // Global parameters
    ...getGlobalParameters()
  };

  // Remove undefined values
  Object.keys(eventData).forEach(key => {
    if (eventData[key] === undefined) delete eventData[key];
  });

  // Send to data layer
  pushToDataLayer(eventData);

  // Development logging
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Form Submit Event:', eventData);
  }
};

/**
 * Track chat interface interactions with standardized parameters
 * @param {string} chatType - Chat interface type (required)
 * @param {Object} options - Additional tracking options
 * @param {string} options.triggerMethod - How chat was opened (button_click, auto, etc.)
 * @param {string} options.chatSessionId - Unique session identifier
 * @param {string} options.userIntent - Detected user intent
 * @param {number} options.previousInteractions - Count of previous chat sessions
 * 
 * @example
 * trackChatOpen('agentic', {
 *   triggerMethod: 'button_click',
 *   userIntent: 'support'
 * });
 */
export const trackChatOpen = (chatType, options = {}) => {
  // Validation
  if (!chatType) {
    console.warn('trackChatOpen: chatType is required');
    return;
  }

  if (!isGTMReady()) {
    console.warn('trackChatOpen: GTM Data Layer not ready');
    return;
  }

  // Build event data
  const eventData = {
    event: 'chat_open',
    event_category: 'engagement',
    event_action: 'open',
    // Required parameters
    chat_type: sanitizeParameterValue(chatType),
    // Optional parameters
    trigger_method: options.triggerMethod ? sanitizeParameterValue(options.triggerMethod) : 'button_click',
    chat_session_id: options.chatSessionId ? sanitizeParameterValue(options.chatSessionId) : undefined,
    user_intent: options.userIntent ? sanitizeParameterValue(options.userIntent) : undefined,
    previous_interactions: options.previousInteractions ? Number(options.previousInteractions) : undefined,
    // Global parameters
    ...getGlobalParameters()
  };

  // Remove undefined values
  Object.keys(eventData).forEach(key => {
    if (eventData[key] === undefined) delete eventData[key];
  });

  // Send to data layer
  pushToDataLayer(eventData);

  // Development logging
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Chat Open Event:', eventData);
  }
};

/**
 * Track section navigation events with standardized parameters
 * @param {string} sectionFrom - Origin section identifier
 * @param {string} sectionTo - Destination section identifier
 * @param {Object} options - Additional tracking options
 * @param {string} options.navigationType - Navigation method (anchor_link, scroll, etc.)
 * @param {string} options.navigationTrigger - What triggered navigation
 * 
 * @example
 * trackSectionNavigation('hero', 'services', {
 *   navigationType: 'anchor_link'
 * });
 */
export const trackSectionNavigation = (sectionFrom, sectionTo, options = {}) => {
  // Validation
  if (!sectionFrom || !sectionTo) {
    console.warn('trackSectionNavigation: sectionFrom and sectionTo are required');
    return;
  }

  if (!isGTMReady()) {
    console.warn('trackSectionNavigation: GTM Data Layer not ready');
    return;
  }

  // Build event data
  const eventData = {
    event: 'section_navigation',
    event_category: 'navigation',
    event_action: 'navigate',
    // Required parameters
    section_from: sanitizeParameterValue(sectionFrom),
    section_to: sanitizeParameterValue(sectionTo),
    // Optional parameters
    navigation_type: options.navigationType ? sanitizeParameterValue(options.navigationType) : 'anchor_link',
    navigation_trigger: options.navigationTrigger ? sanitizeParameterValue(options.navigationTrigger) : undefined,
    // Global parameters
    ...getGlobalParameters()
  };

  // Remove undefined values
  Object.keys(eventData).forEach(key => {
    if (eventData[key] === undefined) delete eventData[key];
  });

  // Send to data layer
  pushToDataLayer(eventData);

  // Development logging
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Section Navigation Event:', eventData);
  }
};

/**
 * Track scroll depth milestones with standardized parameters
 * @param {number} percentage - Scroll percentage (25, 50, 75, 100)
 * @param {Object} options - Additional tracking options
 * @param {string} options.contentType - Type of content being scrolled
 * @param {number} options.timeToReach - Time to reach this depth (seconds)
 * 
 * @example
 * trackScrollDepth(50, {
 *   contentType: 'case_study',
 *   timeToReach: 30
 * });
 */
export const trackScrollDepth = (percentage, options = {}) => {
  // Validation
  if (!percentage || ![25, 50, 75, 100].includes(percentage)) {
    console.warn('trackScrollDepth: percentage must be 25, 50, 75, or 100');
    return;
  }

  if (!isGTMReady()) {
    console.warn('trackScrollDepth: GTM Data Layer not ready');
    return;
  }

  // Build event data
  const eventData = {
    event: 'scroll_depth',
    event_category: 'engagement',
    event_action: 'scroll',
    // Required parameters
    scroll_percentage: Number(percentage),
    // Optional parameters
    content_type: options.contentType ? sanitizeParameterValue(options.contentType) : undefined,
    time_to_reach: options.timeToReach ? Number(options.timeToReach) : undefined,
    // Global parameters
    ...getGlobalParameters()
  };

  // Remove undefined values
  Object.keys(eventData).forEach(key => {
    if (eventData[key] === undefined) delete eventData[key];
  });

  // Send to data layer
  pushToDataLayer(eventData);

  // Development logging
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Scroll Depth Event:', eventData);
  }
};

/**
 * Track link clicks with standardized parameters
 * @param {string} linkUrl - Destination URL
 * @param {string} linkText - Link text content
 * @param {Object} options - Additional tracking options
 * @param {string} options.linkType - Type of link (internal, external, download)
 * @param {string} options.linkCategory - Link category
 * 
 * @example
 * trackLinkClick('https://example.com', 'Learn More', {
 *   linkType: 'external',
 *   linkCategory: 'resource'
 * });
 */
export const trackLinkClick = (linkUrl, linkText, options = {}) => {
  // Validation
  if (!linkUrl || !linkText) {
    console.warn('trackLinkClick: linkUrl and linkText are required');
    return;
  }

  if (!isGTMReady()) {
    console.warn('trackLinkClick: GTM Data Layer not ready');
    return;
  }

  // Determine link type
  const isExternal = linkUrl.startsWith('http') && !linkUrl.includes(window.location.hostname);
  const isDownload = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar)$/i.test(linkUrl);

  // Build event data
  const eventData = {
    event: 'link_click',
    event_category: 'navigation',
    event_action: 'click',
    // Required parameters
    link_url: sanitizeParameterValue(linkUrl),
    link_text: sanitizeParameterValue(linkText),
    // Optional parameters
    link_type: options.linkType || (isDownload ? 'download' : isExternal ? 'external' : 'internal'),
    link_category: options.linkCategory ? sanitizeParameterValue(options.linkCategory) : undefined,
    // Global parameters
    ...getGlobalParameters()
  };

  // Remove undefined values
  Object.keys(eventData).forEach(key => {
    if (eventData[key] === undefined) delete eventData[key];
  });

  // Send to data layer
  pushToDataLayer(eventData);

  // Development logging
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Link Click Event:', eventData);
  }
};

/**
 * Validate event data against standards
 * @param {Object} eventData - Event data to validate
 * @returns {Object} Validation result with errors array
 */
export const validateEventData = (eventData) => {
  const errors = [];
  
  // Check event name
  if (!eventData.event) {
    errors.push('Event name is required');
  } else if (!validateEventName(eventData.event)) {
    errors.push('Event name must be snake_case, lowercase, max 40 characters');
  }
  
  // Check parameter names
  Object.keys(eventData).forEach(param => {
    if (param !== 'event' && !validateParameterName(param)) {
      errors.push(`Parameter '${param}' must be snake_case, lowercase, max 40 characters`);
    }
  });
  
  // Check for PII
  Object.values(eventData).forEach(value => {
    if (typeof value === 'string') {
      if (value.includes('@') && value.includes('.')) {
        errors.push('Potential email address detected in parameters');
      }
      if (/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/.test(value)) {
        errors.push('Potential phone number detected in parameters');
      }
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Export utility functions
export {
  getGlobalParameters,
  getSessionId,
  validateEventName,
  validateParameterName,
  sanitizeParameterValue
};

// Default export with all tracking functions
const standardEvents = {
  trackCtaClick,
  trackFormSubmit,
  trackChatOpen,
  trackSectionNavigation,
  trackScrollDepth,
  trackLinkClick,
  validateEventData,
  getGlobalParameters,
  getSessionId
};

export default standardEvents;